import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import {loadImport} from "../../../api";
import {API_ENDPOINTS, EMPTY_IMPORT, NULL_UUID} from "../../../constants";
import {ArrowUpIcon} from "@heroicons/react/20/solid";
import {useState} from "react";
import {ImportSpreadsheetResponse} from "../../../models/ImportSpreadsheet";
import {CheckCircleIcon, TrashIcon, XCircleIcon} from "@heroicons/react/24/solid";
import {RocketLaunchIcon} from "@heroicons/react/24/outline";
import {ChevronLeftIcon, ChevronRightIcon} from "@heroicons/react/20/solid";
import {MagnifyingGlassIcon} from "@heroicons/react/20/solid";
import {ArrowPathIcon} from "@heroicons/react/20/solid"; // Add this import for the retry icon
import {downloadBlob} from "../../../utilities";
import {ImportResult} from "../../../models/ImportResult";
import ImportResults from "./ImportResults";

interface UploadProps {
    importId: string;
    refetchImport: Function
    setImportSpreadsheet?: Function
}


interface FileUploadStatus {
    file: File;
    status: 'pending' | 'uploading' | 'completed' | 'failed';
    error?: string;
}


function MultiFileUpload(props: UploadProps) {
    const [files, setFiles] = useState<FileList | null>(null);
    const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        setFiles(selectedFiles);

        if (selectedFiles) {
            const statuses: FileUploadStatus[] = Array.from(selectedFiles).map(file => ({
                file,
                status: 'pending'
            }));
            setFileStatuses(statuses);
        } else {
            setFileStatuses([]);
        }
    };

    const updateFileStatus = (fileIndex: number, status: FileUploadStatus['status'], error?: string) => {
        setFileStatuses(prev => prev.map((fileStatus, index) =>
            index === fileIndex
                ? {...fileStatus, status, error}
                : fileStatus
        ));
    };

    // Add retry function for individual files
    const retryFileUpload = async (fileIndex: number) => {
        const fileStatus = fileStatuses[fileIndex];
        if (!fileStatus || fileStatus.status !== 'failed') return;

        // Set status to uploading
        updateFileStatus(fileIndex, 'uploading');

        const formData = new FormData();
        formData.append("file", fileStatus.file);

        try {
            const response = await fetch(API_ENDPOINTS.IMPORT_IMAGE_FILE.replace(":id", props.importId), {
                credentials: 'include',
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                updateFileStatus(fileIndex, 'completed');
                props.refetchImport();
            } else {
                updateFileStatus(fileIndex, 'failed', 'Upload failed');
            }
        } catch (err) {
            console.error(err);
            updateFileStatus(fileIndex, 'failed', 'Network error');
        }
    };

    const handleUpload = async () => {
        if (!files) return;

        setUploading(true);

        // Mark all files as uploading
        setFileStatuses(prev => prev.map(status => ({...status, status: 'uploading' as const})));

        const uploadFile = async (file: File, index: number): Promise<{ success: boolean, index: number }> => {
            const formData = new FormData();
            formData.append("file", file);

            try {
                const response = await fetch(API_ENDPOINTS.IMPORT_IMAGE_FILE.replace(":id", props.importId), {
                    credentials: 'include',
                    method: "POST",
                    body: formData,
                });

                if (response.ok) {
                    updateFileStatus(index, 'completed');
                    return {success: true, index};
                } else {
                    updateFileStatus(index, 'failed', 'Upload failed');
                    return {success: false, index};
                }
            } catch (err) {
                console.error(err);
                updateFileStatus(index, 'failed', 'Network error');
                return {success: false, index};
            }
        };

        // Process uploads with concurrency limit
        const processUploadsWithLimit = async (files: File[], maxConcurrency: number = 10) => {
            const results: { success: boolean, index: number }[] = [];
            let index = 0;

            const executeNext = async (): Promise<void> => {
                if (index >= files.length) return;

                const currentIndex = index++;
                const file = files[currentIndex];

                const result = await uploadFile(file, currentIndex);
                results.push(result);

                // Continue with next file
                await executeNext();
            };

            // Start up to maxConcurrency parallel executions
            const workers = Array(Math.min(maxConcurrency, files.length))
                .fill(null)
                .map(() => executeNext());

            await Promise.all(workers);
            return results;
        };

        try {
            const results = await processUploadsWithLimit(Array.from(files), 10);
            const successfulUploads = results.filter(result => result.success).length;
            const failedUploads = results.length - successfulUploads;

            if (successfulUploads > 0) {
                props.refetchImport();
            }

            // Reset after successful completion of all uploads
            if (failedUploads === 0) {
                setTimeout(() => {
                    setUploading(false);
                    setFiles(null);
                    setFileStatuses([]);

                    // Reset the file input element
                    const fileInput = document.querySelector('input[type="file"][multiple]') as HTMLInputElement;
                    if (fileInput) {
                        fileInput.value = '';
                    }
                }, 1000); // Longer delay to show completion
            } else {
                setUploading(false);
            }
        } catch (err) {
            console.error('Upload error:', err);
            setUploading(false);
        }
    };

    const completedCount = fileStatuses.filter(status => status.status === 'completed').length;
    const failedCount = fileStatuses.filter(status => status.status === 'failed').length;
    const uploadingCount = fileStatuses.filter(status => status.status === 'uploading').length;

    // Show only failed files and top 10 uploading files
    const failedFiles = fileStatuses.filter(status => status.status === 'failed');
    const uploadingFiles = fileStatuses.filter(status => status.status === 'uploading').slice(0, 10);
    const displayFiles = [...failedFiles, ...uploadingFiles];

    return (
        <div>
            <input type="file" multiple onChange={handleFileChange} accept=".png,.jpeg,.jpg"/>

            {files != null && files.length > 0 && (
                <>
                    <button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="ml-6 inline-flex items-center rounded-md bg-blue-600 px-4 py-3 text-md font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50"
                    >
                        <ArrowUpIcon className="-ml-0.5 mr-1.5 h-4 w-4 text-white" aria-hidden="true"/>
                        {uploading ? (
                            `Uploading... (${completedCount}/${files.length} completed${failedCount > 0 ? `, ${failedCount} failed` : ''})`
                        ) : (
                            "Upload Images"
                        )}
                    </button>

                    {/* Show progress section during upload OR when there are failed files */}
                    {(uploading || failedCount > 0) && displayFiles.length > 0 && (
                        <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                                <h4 className="text-sm font-semibold text-gray-900">
                                    {uploading ? (
                                        `Upload Progress (${completedCount}/${files.length} completed)`
                                    ) : (
                                        `Upload Results (${completedCount}/${files.length} completed)`
                                    )}
                                </h4>
                                <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
                                    <span>
                                        {uploading && uploadingCount > 10 ? `Showing 10 of ${uploadingCount} uploading` : `${uploadingCount} uploading`}
                                        {failedCount > 0 && ` • ${failedCount} failed`}
                                    </span>
                                    {uploading && uploadingCount > 10 && (
                                        <span className="text-blue-600">
                                            {uploadingCount - 10} more uploading...
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                                        style={{width: `${(completedCount / files.length) * 100}%`}}
                                    ></div>
                                </div>
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                                <div className="divide-y divide-gray-100">
                                    {/* Show failed files first */}
                                    {failedFiles.map((fileStatus) => {
                                        const fileIndex = fileStatuses.indexOf(fileStatus);
                                        return (
                                            <div key={`failed-${fileIndex}`}
                                                 className="flex items-center justify-between p-3 bg-red-50 hover:bg-red-100">
                                                <div className="flex-1 min-w-0 mr-3">
                                                    <p className="text-sm font-medium text-gray-900 truncate"
                                                       title={fileStatus.file.name}>
                                                        {fileStatus.file.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {(fileStatus.file.size / 1024 / 1024).toFixed(2)} MB
                                                    </p>
                                                </div>
                                                <div className="flex-shrink-0 flex items-center gap-2">
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 cursor-help"
                                                        title={fileStatus.error}
                                                    >
                                                        <XCircleIcon className="h-3 w-3 mr-1"/>
                                                        Failed
                                                    </span>
                                                    <button
                                                        onClick={() => retryFileUpload(fileIndex)}
                                                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                                        title="Try again"
                                                    >
                                                        <ArrowPathIcon className="h-3 w-3 mr-1"/>
                                                        Try Again
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Show top 10 uploading files only when actively uploading */}
                                    {uploading && uploadingFiles.map((fileStatus) => (
                                        <div key={`uploading-${fileStatuses.indexOf(fileStatus)}`}
                                             className="flex items-center justify-between p-3 hover:bg-gray-50">
                                            <div className="flex-1 min-w-0 mr-3">
                                                <p className="text-sm font-medium text-gray-900 truncate"
                                                   title={fileStatus.file.name}>
                                                    {fileStatus.file.name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {(fileStatus.file.size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <div className="flex items-center">
                                                    <div
                                                        className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        Uploading
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Show completion message when no active uploads and no failures */}
                            {uploadingCount === 0 && failedCount === 0 && completedCount > 0 && (
                                <div
                                    className="px-4 py-3 border-t border-gray-200 bg-green-50 rounded-b-lg text-center">
                                    <p className="text-sm text-green-700 font-medium">
                                        ✓ All {completedCount} files uploaded successfully!
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}


const handleSpreadsheetUpload = async (file: any, importId: string, setImportSpreadsheet: Function | undefined, setUploaded: Function) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("spreadsheet", file);
    try {
        const response = await fetch(API_ENDPOINTS.IMPORT_UPLOAD_SPREADSHEET.replace(":id", importId), {
            method: "POST",
            credentials: 'include',
            body: formData,
            referrerPolicy: 'no-referrer',
        });

        if (!response.ok) {
            alert("Error uploading spreadsheet. Please try again.");
            return;
        }

        const rawData = await response.json();
        const data = rawData as ImportSpreadsheetResponse;

        setUploaded(true)

        // @ts-ignore
        setImportSpreadsheet(data);

    } catch (err) {
        console.error(err);
    }

};

const executeImport = async (
    importId: string,
    spreadsheet: ImportSpreadsheetResponse,
    setImportResult: (result: ImportResult | null) => void,
    setIsExecuting: (executing: boolean) => void
): Promise<ImportResult | null> => {
    setIsExecuting(true);
    try {
        const response = await fetch(API_ENDPOINTS.IMPORT_EXECUTE.replace(":id", importId), {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(spreadsheet),
            referrerPolicy: 'no-referrer',
        });

        if (!response.ok) {
            console.error('Import execution failed:', response.status, response.statusText);
            return null;
        }

        const result = await response.json() as ImportResult;
        setImportResult(result);
        return result;

    } catch (err) {
        console.error('Error executing import:', err);
        return null;
    } finally {
        setIsExecuting(false);
    }
};


const ProcessingModal = ({isOpen}: { isOpen: boolean }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">Processing Import</h3>
                    <div className="mt-2 px-7 py-3">
                        <p className="text-sm text-gray-500">
                            Your import is being processed. This may take a few moments...
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};


function SpreadsheetUpload(props: UploadProps) {
    const [file, setFile] = useState<File | null>(null);
    const [uploaded, setUploaded] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };


    return (
        <div>
            <input type="file" onChange={handleFileChange} accept=".xls,.xlsx"/> <br/><br/>
            {file != null && (!uploaded ? <button
                    onClick={() => handleSpreadsheetUpload(file, props.importId, props.setImportSpreadsheet, setUploaded)}
                    className="inline-flex items-center rounded-md bg-purple-600 px-4 py-3 text-md font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600">
                    <ArrowUpIcon className="-ml-0.5 mr-1.5 h-4 w-4 text-white"
                                 aria-hidden="true"/>
                    Upload Spreadsheet
                </button> :

                <button
                    onClick={() => {
                        setFile(null);
                        setUploaded(false);
                        // @ts-ignore
                        props.setImportSpreadsheet(null);
                    }}
                    className="inline-flex items-center rounded-md bg-red-600 px-4 py-3 text-md font-semibold text-white shadow-sm hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
                    <TrashIcon className="-ml-0.5 mr-1.5 h-4 w-4 text-white"
                               aria-hidden="true"/>

                    Reset Spreadsheet
                </button>)}
        </div>
    );
}

export default function SingleImport() {
    const {id} = useParams();

    const [showFiles, setShowFiles] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [importSpreadsheet, setImportSpreadsheet] = useState<ImportSpreadsheetResponse | null>(null);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);

    const [isImportExecuting, setIsImportExecuting] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [errorCurrentPage, setErrorCurrentPage] = useState(1);
    const itemsPerPage = 15;

    const {data: selectedImport, isSuccess: importSuccess, isPending, refetch: refetchImport} = useQuery({
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchOnMount: "always",
        queryKey: [id],
        queryFn: () => loadImport(id || NULL_UUID),
        placeholderData: EMPTY_IMPORT,
        retry: 0,
    });

    function countErrors() {
        let errors = 0;

        if (!importSpreadsheet) {
            return errors;
        }

        importSpreadsheet.forEach((item) => {
            if (item.validationMessages && item.validationMessages.length > 0) {
                errors += item.validationMessages.length;
            }
        })

        return errors;
    }

    // Filter files based on search term
    const filteredFiles = selectedImport?.uploadedFiles?.filter(file =>
        file.replace((id + "/") || "", "").toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const totalFiles = filteredFiles.length;
    const totalPages = Math.ceil(totalFiles / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentFiles = filteredFiles.slice(startIndex, endIndex);

    // Reset to first page when search term changes
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const goToPrevious = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                start = 2;
                end = 4;
            }

            if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
                end = totalPages - 1;
            }

            if (start > 2) {
                pages.push('...');
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (end < totalPages - 1) {
                pages.push('...');
            }

            if (totalPages > 1) {
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const downloadErrorsAsCSV = () => {
        if (!importSpreadsheet) return;

        const errorItems = importSpreadsheet.filter(item => !item.validationMessages || item.validationMessages.length > 0);

        if (errorItems.length === 0) return;

        const csvHeader = 'Spreadsheet Row,Filename,Error Messages\n';

        const csvRows = errorItems.map(item => {
            const row = item.spreadsheetRow || '';
            const filename = item.filename || '';
            const errorMessages = item.validationMessages?.join('; ') || '';

            const escapedFilename = `"${filename.replace(/"/g, '""')}"`;
            const escapedErrorMessages = `"${errorMessages.replace(/"/g, '""')}"`;

            return `${row},${escapedFilename},${escapedErrorMessages}`;
        }).join('\n');

        const csvContent = csvHeader + csvRows;

        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        downloadBlob(blob, `import-errors-${selectedImport?.label || 'unknown'}-${new Date().toISOString().split('T')[0]}.csv`);
    };

    return (
        <>
            {!isPending && <>
                {importSuccess &&
                    <>
                        <div className="container mx-auto">
                            <div
                                className="sticky top-0 flex items-center justify-between bg-white mb-6 px-6 py-12 z-10 border-b-2 border-indigo-600">
                                <div className="min-w-0 flex-1">
                                    <h3 className="text-3xl font-semibold leading-7 text-gray-900 sm:truncate sm:text-4xl sm:tracking-tight">
                                        {selectedImport.label}
                                    </h3>
                                    <p className="mt-1 flex items-center text-sm leading-5 text-gray-500 sm:flex-shrink-0">Provided
                                        By: {selectedImport.providedBy}</p>
                                    <pre
                                        className="mt-6 text-lg font-medium text-gray-900 font-leading">{selectedImport.description}</pre>
                                </div>
                            </div>
                        </div>

                        <main className="px-2 py-2 sm:py-8 lg:px-6">

                            {selectedImport.status === "CLOSED" &&
                                <>
                                    <h1 className="text-2xl font-bold leading-tight text-red-600 mb-6">This import has
                                        been processed and is now closed.</h1>
                                </>}

                            {selectedImport.status !== "CLOSED" &&
                                <>
                                    <h1 className="text-2xl font-bold leading-tight text-red-600 mb-6">NOTE: All images
                                        must
                                        be
                                        uploaded before the spreadsheet.<br/>This is the only way the import tool can
                                        validate
                                        that everything is present.</h1>

                                    <h1 className="text-2xl font-bold leading-tight text-blue-700">NOTE: Steps labeled
                                        with
                                        <em> Upload</em> do not modify the public facing database.
                                        <br/>These steps are exclusively used by
                                        the Import Tool to help you stage new advertisements.
                                        <br/>The <em>Import</em> button that appears once the spreadsheet is validated
                                        will
                                        make
                                        these staged advertisements public. </h1>

                                    <hr className="mt-8 mb-8 border-black border-t-4"/>

                                    <h1 className="text-4xl font-semibold leading-7 mb-6">Step 1: Upload Images</h1>
                                    <MultiFileUpload refetchImport={refetchImport} importId={selectedImport.id}/>

                                    {selectedImport?.uploadedFiles && selectedImport?.uploadedFiles.length > 0 &&
                                        <>
                                            <h3 className="text-2xl font-semibold leading-7 mt-12">{selectedImport?.uploadedFiles.length} Images
                                                Staged for Upload</h3>
                                            <br/>
                                            <button
                                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                                                onClick={() => setShowFiles(!showFiles)}>{showFiles ? "Hide" : "Show"} Uploaded
                                                Images
                                            </button>
                                        </>
                                    }

                                    {selectedImport?.uploadedFiles && selectedImport?.uploadedFiles.length > 0 && showFiles &&
                                        <>
                                            <h4 className="text-3xl font-semibold leading-7 mt-12">Uploaded Images</h4>

                                            {/* Search Box */}
                                            <div className="mt-4 mb-6">
                                                <div className="relative">
                                                    <div
                                                        className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400"
                                                                             aria-hidden="true"/>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder="Search images by filename..."
                                                        value={searchTerm}
                                                        onChange={handleSearchChange}
                                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                    />
                                                </div>
                                                {searchTerm && (
                                                    <p className="mt-2 text-sm text-gray-600">
                                                        {totalFiles === 0 ? (
                                                            `No images found matching "${searchTerm}"`
                                                        ) : (
                                                            `Found ${totalFiles} image${totalFiles === 1 ? '' : 's'} matching "${searchTerm}"`
                                                        )}
                                                    </p>
                                                )}
                                            </div>

                                            {totalFiles > 0 &&
                                                <table className="min-w-full divide-y divide-gray-300 mt-8">
                                                    <thead>
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Filename</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                    {currentFiles.map((file, idx) => (
                                                        <tr key={startIndex + idx}>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{file.replace((id + "/") || "", "")}</td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>}

                                            {/* Pagination Controls */}
                                            {totalPages > 1 && (
                                                <div
                                                    className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                                                    <div className="flex flex-1 justify-between sm:hidden">
                                                        <button
                                                            onClick={goToPrevious}
                                                            disabled={currentPage === 1}
                                                            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            Previous
                                                        </button>
                                                        <button
                                                            onClick={goToNext}
                                                            disabled={currentPage === totalPages}
                                                            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                    <div
                                                        className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                                        <div>
                                                            <p className="text-sm text-gray-700">
                                                                Showing <span
                                                                className="font-medium">{totalFiles > 0 ? startIndex + 1 : 0}</span> to{' '}
                                                                <span
                                                                    className="font-medium">{Math.min(endIndex, totalFiles)}</span> of{' '}
                                                                <span
                                                                    className="font-medium">{totalFiles}</span> results
                                                            </p>
                                                        </div>
                                                        <div>
                                                            <nav
                                                                className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                                                                aria-label="Pagination">
                                                                <button
                                                                    onClick={goToPrevious}
                                                                    disabled={currentPage === 1}
                                                                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <span className="sr-only">Previous</span>
                                                                    <ChevronLeftIcon className="h-5 w-5"
                                                                                     aria-hidden="true"/>
                                                                </button>

                                                                {getPageNumbers().map((page, index) => (
                                                                    page === '...' ? (
                                                                        <span
                                                                            key={`ellipsis-${index}`}
                                                                            className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                                                                        >
                                                                    ...
                                                                </span>
                                                                    ) : (
                                                                        <button
                                                                            key={page}
                                                                            onClick={() => goToPage(page as number)}
                                                                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                                                                currentPage === page
                                                                                    ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                                                                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                                                            }`}
                                                                        >
                                                                            {page}
                                                                        </button>
                                                                    )
                                                                ))}

                                                                <button
                                                                    onClick={goToNext}
                                                                    disabled={currentPage === totalPages}
                                                                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <span className="sr-only">Next</span>
                                                                    <ChevronRightIcon className="h-5 w-5"
                                                                                      aria-hidden="true"/>
                                                                </button>
                                                            </nav>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </>}


                                    {selectedImport?.uploadedFiles && selectedImport?.uploadedFiles.length > 0 &&
                                        <>
                                            <hr className="mt-8 mb-8 border-black border-t-4"/>

                                            <h1 className="text-4xl font-semibold text-gray-900 mb-4">Step 2:
                                                Spreadsheet
                                                Upload</h1>
                                            <SpreadsheetUpload refetchImport={refetchImport}
                                                               importId={selectedImport.id}
                                                               setImportSpreadsheet={setImportSpreadsheet}/>

                                            {importSpreadsheet &&
                                                <>
                                                    <hr className="mt-8 mb-8 border-black border-t-4"/>
                                                    <h1 className="text-4xl font-semibold leading-7 mb-6">Step 3:
                                                        Spreadsheet
                                                        Validation</h1>

                                                    <h3 className="text-2xl font-semibold text-black mb-4">{importSpreadsheet?.length} Advertisement{importSpreadsheet?.length === 1 ? ' ' : 's '}
                                                        Defined</h3>
                                                    {countErrors() > 0 && <>
                                                        <h3 className="text-2xl font-bold text-black mb-4">{countErrors()} Total
                                                            Errors</h3>
                                                        <h3 className="text-2xl font-bold text-black mb-4">Please
                                                            correct
                                                            these
                                                            errors, click the Reset Spreadsheet button and reupload
                                                            the
                                                            spreadsheet.</h3>

                                                        <div className="flex gap-4 mb-4">
                                                            <button
                                                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                                                                onClick={() => setShowErrors(!showErrors)}>{showErrors ? "Hide " : "Show "}
                                                                Errors
                                                            </button>
                                                            <button
                                                                className="bg-orange-700 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded"
                                                                onClick={downloadErrorsAsCSV}
                                                            >
                                                                Download Errors as CSV
                                                            </button>
                                                        </div>
                                                    </>}

                                                    {countErrors() > 0 && showErrors && <>
                                                        <hr className="mt-8 mb-8 border-black border-t-2"/>
                                                        <h2 className="text-3xl font-semibold text-gray-900 mb-4">Spreadsheet
                                                            Errors</h2>

                                                        {(() => {
                                                            // Filter items with errors
                                                            const errorItems = importSpreadsheet.filter(item => !item.validationMessages || item.validationMessages.length > 0);
                                                            const totalErrorItems = errorItems.length;
                                                            const errorItemsPerPage = 15;
                                                            const totalErrorPages = Math.ceil(totalErrorItems / errorItemsPerPage);
                                                            const errorStartIndex = (errorCurrentPage - 1) * errorItemsPerPage;
                                                            const errorEndIndex = errorStartIndex + errorItemsPerPage;
                                                            const currentErrorItems = errorItems.slice(errorStartIndex, errorEndIndex);

                                                            const goToErrorPage = (page: number) => {
                                                                setErrorCurrentPage(page);
                                                            };

                                                            const goToErrorPrevious = () => {
                                                                if (errorCurrentPage > 1) {
                                                                    setErrorCurrentPage(errorCurrentPage - 1);
                                                                }
                                                            };

                                                            const goToErrorNext = () => {
                                                                if (errorCurrentPage < totalErrorPages) {
                                                                    setErrorCurrentPage(errorCurrentPage + 1);
                                                                }
                                                            };

                                                            const getErrorPageNumbers = () => {
                                                                const pages = [];
                                                                const maxVisiblePages = 5;

                                                                if (totalErrorPages <= maxVisiblePages) {
                                                                    for (let i = 1; i <= totalErrorPages; i++) {
                                                                        pages.push(i);
                                                                    }
                                                                } else {
                                                                    pages.push(1);

                                                                    let start = Math.max(2, errorCurrentPage - 1);
                                                                    let end = Math.min(totalErrorPages - 1, errorCurrentPage + 1);

                                                                    if (errorCurrentPage <= 3) {
                                                                        start = 2;
                                                                        end = 4;
                                                                    }

                                                                    if (errorCurrentPage >= totalErrorPages - 2) {
                                                                        start = totalErrorPages - 3;
                                                                        end = totalErrorPages - 1;
                                                                    }

                                                                    if (start > 2) {
                                                                        pages.push('...');
                                                                    }

                                                                    for (let i = start; i <= end; i++) {
                                                                        pages.push(i);
                                                                    }

                                                                    if (end < totalErrorPages - 1) {
                                                                        pages.push('...');
                                                                    }

                                                                    if (totalErrorPages > 1) {
                                                                        pages.push(totalErrorPages);
                                                                    }
                                                                }

                                                                return pages;
                                                            };

                                                            return (
                                                                <>
                                                                    <table
                                                                        className="min-w-full divide-y divide-gray-300 mt-8">
                                                                        <thead>
                                                                        <tr>
                                                                            <th className="px-6 py-3 text-left text-md font-semibold text-gray-900">Spreadsheet
                                                                                Row
                                                                            </th>
                                                                            <th className="px-6 py-3 text-left text-md font-semibold text-gray-900">Filename</th>
                                                                            <th className="px-6 py-3 text-left text-md font-semibold text-gray-900">Validation
                                                                                Messages
                                                                            </th>
                                                                            <th className="px-6 py-3 text-left text-md font-semibold text-gray-900">Ready
                                                                                for Import?
                                                                            </th>
                                                                        </tr>
                                                                        </thead>
                                                                        <tbody className="divide-y divide-gray-200">
                                                                        {currentErrorItems.map((item, idx) => (
                                                                            <tr key={errorStartIndex + idx}>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-md text-black font-bold">Row
                                                                                    #{item.spreadsheetRow}</td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-md text-black font-bold">
                                                                                    {item.filename}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-md text-gray-900">
                                                                                    <ul className="list-disc list-inside">
                                                                                        {item?.validationMessages && item.validationMessages.map((msg, msgIdx) => (
                                                                                            <li className="whitespace-nowrap text-md text-black font-bold"
                                                                                                key={msgIdx}>
                                                                                                {msg}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-md text-gray-900">
                                                                                    {!item?.validationMessages || item?.validationMessages?.length > 0 ?
                                                                                        <XCircleIcon
                                                                                            className="w-12 h-12 text-red-600"/> :
                                                                                        <CheckCircleIcon
                                                                                            className="w-12 h-12 text-green-600"/>}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                        </tbody>
                                                                    </table>

                                                                    {/* Pagination Controls for Errors */}
                                                                    {totalErrorPages > 1 && (
                                                                        <div
                                                                            className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4">
                                                                            <div
                                                                                className="flex flex-1 justify-between sm:hidden">
                                                                                <button
                                                                                    onClick={goToErrorPrevious}
                                                                                    disabled={errorCurrentPage === 1}
                                                                                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                >
                                                                                    Previous
                                                                                </button>
                                                                                <button
                                                                                    onClick={goToErrorNext}
                                                                                    disabled={errorCurrentPage === totalErrorPages}
                                                                                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                >
                                                                                    Next
                                                                                </button>
                                                                            </div>
                                                                            <div
                                                                                className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                                                                <div>
                                                                                    <p className="text-sm text-gray-700">
                                                                                        Showing <span
                                                                                        className="font-medium">{totalErrorItems > 0 ? errorStartIndex + 1 : 0}</span> to{' '}
                                                                                        <span
                                                                                            className="font-medium">{Math.min(errorEndIndex, totalErrorItems)}</span> of{' '}
                                                                                        <span
                                                                                            className="font-medium">{totalErrorItems}</span> error{totalErrorItems === 1 ? '' : 's'}
                                                                                    </p>
                                                                                </div>
                                                                                <div>
                                                                                    <nav
                                                                                        className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                                                                                        aria-label="Pagination">
                                                                                        <button
                                                                                            onClick={goToErrorPrevious}
                                                                                            disabled={errorCurrentPage === 1}
                                                                                            className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        >
                                                                                    <span
                                                                                        className="sr-only">Previous</span>
                                                                                            <ChevronLeftIcon
                                                                                                className="h-5 w-5"
                                                                                                aria-hidden="true"/>
                                                                                        </button>

                                                                                        {getErrorPageNumbers().map((page, index) => (
                                                                                            page === '...' ? (
                                                                                                <span
                                                                                                    key={`error-ellipsis-${index}`}
                                                                                                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                                                                                                >
                                                                                            ...
                                                                                        </span>
                                                                                            ) : (
                                                                                                <button
                                                                                                    key={`error-page-${page}`}
                                                                                                    onClick={() => goToErrorPage(page as number)}
                                                                                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                                                                                        errorCurrentPage === page
                                                                                                            ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                                                                                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                                                                                    }`}
                                                                                                >
                                                                                                    {page}
                                                                                                </button>
                                                                                            )
                                                                                        ))}

                                                                                        <button
                                                                                            onClick={goToErrorNext}
                                                                                            disabled={errorCurrentPage === totalErrorPages}
                                                                                            className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        >
                                                                                    <span
                                                                                        className="sr-only">Next</span>
                                                                                            <ChevronRightIcon
                                                                                                className="h-5 w-5"
                                                                                                aria-hidden="true"/>
                                                                                        </button>
                                                                                    </nav>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </>}
                                                </>
                                            }

                                            {importSpreadsheet && countErrors() === 0 && (
                                                <button
                                                    onClick={() => executeImport(id!, importSpreadsheet, setImportResult, setIsImportExecuting)}
                                                    disabled={isImportExecuting}
                                                    className="inline-flex items-center rounded-md bg-green-600 px-4 py-3 text-md font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <RocketLaunchIcon className="-ml-0.5 mr-1.5 h-4 w-4 text-white"
                                                                      aria-hidden="true"/>
                                                    {isImportExecuting ? 'Processing...' : 'Execute Import'}
                                                </button>
                                            )}

                                            {importResult && (
                                                <ImportResults
                                                    result={importResult}
                                                    onClose={() => setImportResult(null)}
                                                />
                                            )}

                                        </>}
                                </>}

                            <ProcessingModal isOpen={isImportExecuting}/>

                        </main>
                    </>
                }
            </>
            }
        </>
    );
}